import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sprout, Wrench, Coins, ArrowRight, Check } from "lucide-react";
import { AUTH } from "@/constants/testIds";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const roles = [
  { key: "farmer", label: "Farmer", icon: Sprout, color: "emerald", tid: AUTH.roleFarmer,
    desc: "Sell your produce, buy equipment, hire farm labour." },
  { key: "day_worker", label: "Day Worker", icon: Wrench, color: "blue", tid: AUTH.roleWorker,
    desc: "Offer your services — electrician, driver, plumber and more." },
  { key: "investor", label: "Investor", icon: Coins, color: "amber", tid: AUTH.roleInvestor,
    desc: "Fund startups, ideas, and projects across India." },
];

const colorMap = {
  emerald: { ring: "ring-emerald-500", bg: "bg-emerald-600", soft: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-300" },
  blue: { ring: "ring-blue-500", bg: "bg-blue-600", soft: "bg-blue-50", text: "text-blue-700", border: "border-blue-300" },
  amber: { ring: "ring-amber-500", bg: "bg-amber-500", soft: "bg-amber-50", text: "text-amber-700", border: "border-amber-300" },
};

export default function RoleSelect() {
  const nav = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const [selected, setSelected] = useState(location.state?.preselect || null);

  const localized = [
    { ...roles[0], label: t("role.farmer"), desc: t("role.farmer.desc") },
    { ...roles[1], label: t("role.worker"), desc: t("role.worker.desc") },
    { ...roles[2], label: t("role.investor"), desc: t("role.investor.desc") },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF7] px-6 md:px-12 lg:px-24 py-12">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold tracking-[0.24em] uppercase text-emerald-700">{t("auth.step01")}</div>
          <LanguageSwitcher />
        </div>
        <h1 className="mt-3 text-4xl md:text-6xl font-bold text-gray-900 tracking-tight">
          {t("auth.welcome_gtf")}
        </h1>
        <p className="mt-4 text-lg text-gray-600 max-w-xl">{t("auth.build_future")}</p>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {localized.map((r, i) => {
            const isSel = selected === r.key;
            const c = colorMap[r.color];
            return (
              <motion.button key={r.key}
                initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                data-testid={r.tid} onClick={() => setSelected(r.key)}
                className={`text-left rounded-3xl p-8 bg-white border-2 transition-all ${isSel ? `${c.border} ring-4 ${c.ring}/30 -translate-y-1` : "border-gray-100 hover:border-gray-200 hover:-translate-y-1"} premium-shadow`}>
                <div className="flex items-center justify-between">
                  <div className={`h-14 w-14 rounded-2xl ${c.soft} flex items-center justify-center ${c.text}`}>
                    <r.icon className="h-7 w-7" />
                  </div>
                  {isSel && <div className={`h-8 w-8 rounded-full ${c.bg} text-white flex items-center justify-center`}><Check className="h-5 w-5" /></div>}
                </div>
                <div className="mt-6 text-2xl font-bold text-gray-900">{r.label}</div>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{r.desc}</p>
              </motion.button>
            );
          })}
        </div>

        <div className="mt-14 flex items-center justify-between">
          <button onClick={() => nav("/")} className="text-gray-500 hover:text-gray-800 text-sm">← {t("auth.back")}</button>
          <button
            disabled={!selected}
            onClick={() => nav("/register", { state: { role: selected } })}
            className={`inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-all ${selected ? "bg-emerald-600 text-white hover:bg-emerald-700 shadow-xl" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}>
            {t("hero.get_started")} <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
